import { useState } from "react";
import { useQuery } from "react-query";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MultiSelect from "src/components/common/MultiSelect";
import {
  EntityCostSummary,
  getEntityCostSummary,
} from "src/utils/api/dashboard";
import { downloadSheet, generateExportFilename } from "src/utils/helper";

const numericColumns = [
  { field: "totalLandNumber", label: "Total Land Number" },
  { field: "finalArea", label: "Final Area (ha)" },
  { field: "demandNoteCost", label: "Demand Note Cost (₹)" },
  { field: "ecoRestorationCost", label: "Eco-Restoration Cost (₹)" },
  { field: "totalReceived", label: "Total Received (₹)" },
  { field: "releasedAmount", label: "Fund Release Amount (₹)" },
] as const;

const formatNumber = (value: number, field: string) =>
  value.toLocaleString("en-IN", {
    minimumFractionDigits: field === "totalLandNumber" ? 0 : 2,
    maximumFractionDigits: field === "totalLandNumber" ? 0 : 2,
  });

const EntityDetailV2 = () => {
  const [entityIds, setEntityIds] = useState<string[]>([]);
  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    ["entityCostSummary"],
    getEntityCostSummary
  );
  const allRows = data?.data?.rows ?? [];
  const rows = allRows.filter(
    (row) => !entityIds.length || entityIds.includes(row.entityId)
  );
  const totals = rows.reduce(
    (sum, row) => {
      numericColumns.forEach(({ field }) => {
        sum[field] += row[field];
      });
      return sum;
    },
    {
      totalLandNumber: 0,
      finalArea: 0,
      demandNoteCost: 0,
      ecoRestorationCost: 0,
      totalReceived: 0,
      releasedAmount: 0,
    }
  );

  const renderNumbers = (
    row: Pick<EntityCostSummary, (typeof numericColumns)[number]["field"]>
  ) =>
    numericColumns.map(({ field }) => (
      <TableCell
        key={field}
        align="right"
        sx={{
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatNumber(row[field], field)}
      </TableCell>
    ));

  const handleExport = () => {
    const exportRows = [...rows, { entityName: "Total", ...totals }].map(
      (row) => {
        const record: Record<string, string | number> = {
          "Entity Name": row.entityName,
        };
        numericColumns.forEach(({ field, label }) => {
          record[label] = row[field];
        });
        return record;
      }
    );
    downloadSheet(exportRows, generateExportFilename("entity-detail-v2"));
  };

  return (
    <Box sx={{ px: 1, pb: 1, mt: -1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontSize: 16, lineHeight: 1.4 }}>
            Entity Detail-v2
          </Typography>
          <Typography
            sx={{ fontSize: 12, lineHeight: 1.4 }}
            color="text.secondary"
          >
            Totals for selected land parcels in Cost Estimates.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <MultiSelect
            label="Filter By Entity"
            options={allRows.map((row) => ({
              id: row.entityId,
              name: row.entityName,
            }))}
            value={entityIds}
            onChange={setEntityIds}
            clearable
            sx={{
              m: 0,
              width: { xs: 220, sm: 260 },
              "& .MuiInputBase-root": { height: 34, fontSize: 13 },
              "& .MuiSelect-select": { py: 0.75, pl: 1.25 },
              "& .MuiInputLabel-root": {
                top: 0,
                left: 0,
                transform: "translate(10px, 7px)",
                fontSize: 13,
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={isFetching || isError || !rows.length}
            size="small"
            sx={{ height: 34, px: 1.5, fontSize: 12, whiteSpace: "nowrap" }}
          >
            Export to Excel
          </Button>
        </Box>
      </Box>
      {isFetching && <LinearProgress aria-label="Loading entity details" />}
      {isError ? (
        <Alert
          severity="error"
          action={<Button onClick={() => refetch()}>Retry</Button>}
        >
          Unable to load entity details. Please try again.
        </Alert>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ overflow: "visible" }}
        >
          <Table
            aria-label="Entity Detail-v2"
            size="small"
            sx={{
              width: "100%",
              tableLayout: "fixed",
              "& .MuiTableCell-root": {
                px: 1,
                py: 0.5,
                typography: "body2",
              },
              "& .MuiTableCell-head": {
                py: 0.75,
                backgroundColor: "#e9eaec",
              },
              "& .MuiTableCell-head, & .MuiTableCell-footer": {
                fontWeight: 700,
              },
              "& .MuiTableBody-root .MuiTableCell-root:first-of-type": {
                overflowWrap: "anywhere",
              },
            }}
          >
            <colgroup>
              <col style={{ width: "28%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
            </colgroup>
            <TableHead sx={{ backgroundColor: "#e9eaec" }}>
              <TableRow>
                <TableCell>Entity Name</TableCell>
                {numericColumns.map(({ field, label }) => (
                  <TableCell key={field} align="right">
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.entityId} hover>
                  <TableCell component="th" scope="row">
                    {row.entityName}
                  </TableCell>
                  {renderNumbers(row)}
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    {isLoading
                      ? "Loading entity details…"
                      : "No entity records found."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {rows.length > 0 && (
              <TableFooter
                sx={{
                  backgroundColor: "#eef8f7",
                  "& .MuiTableCell-root": {
                    backgroundColor: "#eef8f7",
                    color: "text.primary",
                  },
                }}
              >
                <TableRow>
                  <TableCell component="th" scope="row">
                    Total
                  </TableCell>
                  {renderNumbers(totals)}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default EntityDetailV2;
